import React, { useState } from 'react';
import {
  formatEstimationTime,
  getAddressFormatted,
  getDateFormat,
  getEstimationTime,
  getShopFromOrder,
  getStatus,
  isDarkTheme,
  isOrderRefunded,
} from '../../../utils';
import {
  ORDER_PHASE,
  ORDER_PHASE_DELIVERING,
  ORDER_PHASE_TAKEAWAY,
  ORDER_TYPE_CLICK_AND_SEAT,
  ORDER_TYPE_DELIVERY,
  UNIT,
} from '../../../constants';
import { useAppSelector } from '../../../redux/hook';
import OrderTotal from './OrderTotal';
import OrderItem from '../../OrderItem';
import OrderStatusIndicator from './OrderStatusIndicator';

type OrderDetailProps = {
  order: any;
  showFees: boolean;
  deleteOption: boolean;
};

export default function OrderDetail({
  order,
  showFees,
  deleteOption,
}: OrderDetailProps) {
  const [border, setBorder] = useState('transparent');
  const { secondaryColor } = useAppSelector(
    (state) => state.configurationReducer,
  );

  const { orderNumber, date, commande, status, orderType } = order;
  const shop = getShopFromOrder(order);
  const estimationTime = getEstimationTime(order);
  const {
    estimationTimeFormat,
    estimationDayFormat,
    estimationTimeFormatWithMargin,
  } = formatEstimationTime(estimationTime);
  const { name, address } = shop;
  const isPastOrder = !!(
    status === ORDER_PHASE.COMPLETED ||
    status === ORDER_PHASE.REFUND ||
    order.cancellation
  );
  let orderTypeLabel = '';
  if (orderType) {
    if (orderType === ORDER_TYPE_DELIVERY) {
      orderTypeLabel = 'En livraison';
    } else if (orderType === ORDER_TYPE_CLICK_AND_SEAT) {
      orderTypeLabel = 'Sur place';
    } else {
      orderTypeLabel = `À emporter`;
    }
  }

  const toggleHover = () => {
    setBorder(secondaryColor);
  };

  const toggleLeave = () => {
    setBorder('transparent');
  };

  const renderItems = (commande) => {
    const nodes: React.ReactNode[] = [];

    Object.keys(commande).forEach((key) => {
      nodes.push(
        <OrderItem
          cartItem={commande[key]}
          key={key}
          cartItemKey={key}
          deleteOption={deleteOption}
        />,
      );
    });

    return nodes;
  };

  return (
    <div
      className='card mb-3 shadow'
      onMouseEnter={() => toggleHover()}
      onMouseLeave={() => toggleLeave()}
      style={{
        cursor: isPastOrder ? 'unset' : 'pointer',
        border: isPastOrder && !isDarkTheme() ? 'none' : `2px solid ${border}`,
        borderRadius: UNIT * 2.1875,
        position: 'relative',
      }}
    >
      {isPastOrder && <OrderStatusIndicator order={order} />}
      <div
        className='card-body dark-modal'
        style={{ borderRadius: UNIT * 2 }}
      >
        <h5
          className='card-title label'
          style={{ color: secondaryColor }}
        >
          Commande n°
          <span className='no-localization label'>{orderNumber}</span>
        </h5>
        <h6 className='card-subtitle mb-2 text-muted no-localization label'>
          {getDateFormat(date)} - {orderTypeLabel}
        </h6>
        <h6 className='card-subtitle mb-2 text-muted no-localization label'>
          {orderType === ORDER_TYPE_DELIVERY
            ? `Adresse : ${getAddressFormatted(order.address)}`
            : `${name} : ${getAddressFormatted(address)}`}
        </h6>
        <hr className='my-4 hr' />
        <div className='container-block'>
          {isPastOrder && renderItems(commande)}
          <OrderTotal
            commande={order.commande}
            promotion={order.promotion}
            showFees={showFees}
            marketingCampaignPromoAmount={order.marketingCampaignPromoAmount}
            totalPriceOrder={order.totalPriceOrder}
            managementFee={order.managementFee}
            totalProductPriceOrder={order.totalProductPriceOrder}
            isOrderFreeDelivery={order.freeDelivery}
            orderUserServicePrice={order.userServicePrice}
          />
          {!isPastOrder && (
            <>
              <hr className='my-4 hr' />
              <b className='text-left'>{getStatus(order)}</b>
              {status !== ORDER_PHASE_TAKEAWAY &&
                status !== ORDER_PHASE_DELIVERING && (
                  <div className='text-left'>{`Commande prévue pour : ${estimationDayFormat} ${
                    order.isSchedule
                      ? `à ${estimationTimeFormat}`
                      : estimationTimeFormatWithMargin
                  }`}</div>
                )}
            </>
          )}
          {isOrderRefunded(order) && (
            <div style={{ color: 'red', fontSize: 12, textAlign: 'center' }}>
              Cette commande a été remboursée. Les fonds peuvent prendre jusqu’à
              7 jours pour apparaître sur votre compte.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
